// src/App.tsx
import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline, Box, CircularProgress, Typography } from "@mui/material";

import { UIProvider } from "./contexts/UIContext";
import { AppShell } from "./components/AppShell";
import { DesktopGate } from "./components/DesktopGate";
import { Logo } from "./components/Logo";
import { APIProvider } from "@vis.gl/react-google-maps";
import { ChatInterface } from "./components/ChatInterface";
import { GuidePage } from "./pages/GuidePage";
import { FeedPage } from "./pages/FeedPage";
import { ProfilePage } from "./pages/ProfilePage";
import { VisitToFeedMount } from "./feed/VisitToFeedMount";
import { UsernameClaimModal } from "./components/profile/UsernameClaimModal";
import { DiscoverPage } from "./pages/DiscoverPage";
import { MapPage } from "./pages/MapPage";
import { SharedCardPage } from "./pages/SharedCardPage";
import { CuratorLog } from "./pages/CuratorLog";
import CollectionsPage from "./pages/CollectionsPage";
import { useGuideBackground } from "./hooks/useGuide";
import { useGuideFeedBackground } from "./hooks/useGuideFeed";
import { CookieBanner } from "./components/gdpr/CookieBanner";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Settings } from "./pages/Settings";

import { useAuthStore } from "./stores/authStore";
import { useFeatureAccess } from "./hooks/useFeatureAccess";
import { OnboardingDialog } from "./components/onboarding/OnboardingDialog";
import { AuthModalHost } from "./components/auth/AuthModalHost";
import { LanguageGate } from "./components/onboarding/LanguageGate";
import {
  getLastConversationId,
  setLastConversationId,
} from "./services/lastConversation";
import {
  startLocationTracking,
  stopLocationTracking,
} from "./services/locationTrackingService";
import { useT } from './i18n/useT';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Companion-feeling theme: deeper primary, warmer accent, grounded neutrals.
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#0F4C75", // deep ocean blue
      light: "#3282B8",
      dark: "#093553",
    },
    secondary: {
      main: "#E07A5F", // terracotta
      light: "#F2A48E",
      dark: "#B85C44",
    },
    background: {
      default: "#FAFAF7",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#555555",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600, letterSpacing: "-0.01em" },
    h6: { fontWeight: 600, letterSpacing: "-0.005em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: "none" } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none" } },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 0,
          paddingTop: 8,
          "&.Mui-selected": {
            paddingTop: 8,
            fontWeight: 600,
          },
        },
        label: {
          fontSize: 11,
          marginTop: 2,
          "&.Mui-selected": { fontSize: 11 },
        },
      },
    },
  },
});

// ---- Route components ----

function FullPageLoader({ label }: { label: string }) {
  const t = useT();
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: "100dvh",
        "@supports not (height: 100dvh)": { height: "100vh" },
      }}
      gap={3}
      bgcolor="background.default"
    >
      {/* Logo featured on the splash. The logo is the strongest brand
          signal we have — using it here instead of just a generic
          spinner makes startup feel like our app rather than a
          framework default. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // Subtle entrance so it doesn't feel like a static asset
          animation: "splashFadeIn 350ms ease-out",
          "@keyframes splashFadeIn": {
            "0%": { opacity: 0, transform: "translateY(6px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Logo size={64} alt={t("Instant Tours")} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          mt: 1,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const t = useT();
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) return <FullPageLoader label={t("Loading…")} />;
  if (!isAuthenticated) return <Navigate to="/chat" replace />;
  return <>{children}</>;
}

/**
 * Custom-claim-gated route. Composes with ProtectedRoute: the
 * inner content only renders if the user is authenticated AND has
 * the named feature claim granted via the Admin SDK
 * (see scripts/manageFeatureAccess.mjs).
 *
 * Behavior:
 *   - Loading → FullPageLoader (auth + token-refresh in flight)
 *   - Not authenticated → /chat (handled upstream by ProtectedRoute)
 *   - Authenticated but missing claim → silent redirect to /
 *
 * Defense-in-depth: the Firestore rule on /curator_decisions also
 * checks request.auth.token.features.memoryLog, so even a user
 * who manually navigates to /curator-log won't see anyone's data.
 * This guard is here to give them a clean redirect rather than
 * an empty list with permission errors in the console.
 */
function FeatureRoute({
  flag,
  children,
}: {
  flag: 'memoryLog' | 'admin';
  children: React.ReactNode;
}) {
  const t = useT();
  const { features, isLoading } = useFeatureAccess();
  if (isLoading) return <FullPageLoader label={t("Loading…")} />;
  if (!features[flag]) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Smart root landing — first-session users land on Guide (where the
 * curated cards live and the app's value is most visible), returning
 * users resume their last conversation. We base "first session" on
 * whether there's a stored last-conversation id for this uid; absent
 * one means they've never started a chat (or cleared local storage
 * recently). Either way, Guide is a better "now what?" surface than
 * an empty welcome screen for a brand-new user.
 */
function RootLandingRoute() {
  const { user } = useAuthStore();
  const uid = user?.uid;
  if (!uid) return <Navigate to="/chat" replace />;
  const last = getLastConversationId(uid);
  if (last) {
    return <Navigate to={`/chat/${last}`} replace />;
  }
  return <Navigate to="/guide" replace />;
}

function ChatRoute() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const uid = user?.uid;

  // The Sidebar's "New Chat" button navigates to /chat with
  // state: { freshChat: true } expressly so the user lands on the
  // WelcomeScreen / conversation-type picker. Without honoring this
  // flag the auto-resume effect below would immediately bounce the
  // user back to their last conversation and the picker would never
  // render. State is per-navigation so this only suppresses the one
  // intended landing; subsequent /chat visits resume normally.
  const freshChat = (location.state as { freshChat?: boolean } | null)
    ?.freshChat === true;

  // Auto-resume: if the URL has no conversationId but we have a stored
  // last one for this user, redirect to that. Skipped when freshChat
  // is set (see above). The conversation might have been deleted
  // server-side; in that case useChat will simply return no messages
  // and the user can start typing as if it's new.
  useEffect(() => {
    if (conversationId) return;
    if (!uid) return;
    if (freshChat) return;
    const last = getLastConversationId(uid);
    if (last) {
      navigate(`/chat/${last}`, { replace: true });
    }
  }, [conversationId, uid, navigate, freshChat]);

  // Persist whatever conversation we're viewing, so revisits to /chat
  // resume here. We don't persist null — a fresh-conversation visit
  // shouldn't blow away the previous resume target until the user
  // actually sends a message and a new id is created (handled below).
  useEffect(() => {
    if (uid && conversationId) {
      setLastConversationId(uid, conversationId);
    }
  }, [uid, conversationId]);

  const handleConversationCreated = (id: string) => {
    if (uid) setLastConversationId(uid, id);
    navigate(`/chat/${id}`);
  };

  return (
    <AppShell currentConversationId={conversationId ?? null}>
      <ChatInterface
        conversationId={conversationId ?? null}
        onConversationCreated={handleConversationCreated}
      />
    </AppShell>
  );
}

function GuideRoute() {
  return (
    <AppShell currentConversationId={null}>
      <GuidePage />
    </AppShell>
  );
}

function DiscoverRoute() {
  return (
    <AppShell currentConversationId={null}>
      <DiscoverPage />
    </AppShell>
  );
}

function FeedRoute() {
  return (
    <AppShell currentConversationId={null}>
      <FeedPage />
    </AppShell>
  );
}

function ProfileRoute() {
  return (
    <AppShell currentConversationId={null}>
      <ProfilePage />
    </AppShell>
  );
}

function MapRoute() {
  return (
    <AppShell currentConversationId={null}>
      <MapPage />
    </AppShell>
  );
}

function CollectionsRoute() {
  return (
    <AppShell currentConversationId={null}>
      <CollectionsPage />
    </AppShell>
  );
}

// Memory log — Phase 3C. Read-only feed of curator decisions.
// Lives in AppShell like the other top-level pages so the sidebar
// stays accessible for navigation away. Registered-only at the
// menu level (anonymous users have no curator-managed memory),
// but the route is also gated by ProtectedRoute below.
function CuratorLogRoute() {
  return (
    <AppShell currentConversationId={null}>
      <CuratorLog />
    </AppShell>
  );
}

// ---- App root ----

function AppContent() {
  const t = useT();
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const {
    isAuthenticated,
    isLoading: authLoading,
    initializeAuth,
    profile,
    authMode,
  } = useAuthStore();

  // Onboarding dialog is shown to registered users who haven't yet
  // completed their travel profile AND haven't explicitly skipped.
  // Once dismissed (via skip or completion), the trigger fields on
  // their profile flip and the dialog stops appearing.
  //
  // Timer race fix: previously the 800ms setTimeout would still
  // fire after auth flipped (e.g. user signs out within the
  // window) because the cleanup only ran on the next effect
  // re-run. The cleanup now also clears on unmount, and we
  // guard the setShowOnboarding call against `cancelled`. A
  // no-op when the user has already navigated away.
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) {
      setShowOnboarding(false);
      return;
    }
    if (authMode !== "registered") return;
    if (!profile) return;
    const tp = profile.travelProfile;
    const isCompleted = !!tp?.completedAt;
    const wasSkipped = !!tp?.skippedOnboardingAt;
    if (!isCompleted && !wasSkipped) {
      // Slight delay so the chat tab can render first, making the
      // onboarding feel like a layer over the app rather than a
      // gate before it. 800ms is enough for the user to see
      // they've landed somewhere meaningful.
      let cancelled = false;
      const t = setTimeout(() => {
        if (!cancelled) setShowOnboarding(true);
      }, 800);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }
  }, [isAuthenticated, authMode, profile]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Public share-recipient page: /guide/:shareId. Render this BEFORE
  // the auth gate so an unauthenticated visitor doesn't see the
  // "Starting Instant Tours…" loader while we wait for auth — they
  // can read the shared card immediately. /privacy and /terms are
  // also public.
  //
  // Live: BrowserRouter wraps AppContent (see App below), so this
  // re-evaluates on every client-side navigation. Previously it
  // read window.location.pathname at render time and was stuck
  // on the initial value until hard reload.
  const routerLocation = useLocation();
  const path = routerLocation.pathname;
  const isPublicPath =
    path.startsWith("/guide/") ||
    path === "/privacy" ||
    path === "/terms";

  // Desktop/tablet gating value — read once at the top of AppContent
  // and applied at each of the three return paths below. The check
  // evaluates inside DesktopGate against the live viewport, so a
  // resize re-renders correctly. The override field is at the root
  // of the user's Firestore doc (intentionally not in the UI; toggle
  // in the Firestore console).
  const allowDesktop = !!profile?.allowDesktop;

  if (isPublicPath) {
    return (
      <DesktopGate allowDesktop={allowDesktop}>
        <Routes>
          <Route path="/guide/:shareId" element={<SharedCardPage />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </DesktopGate>
    );
  }

  // Location tracking tied to auth
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const started = startLocationTracking();
      console.log("[App] Location tracking started:", started);
      return () => {
        stopLocationTracking();
        console.log("[App] Location tracking stopped");
      };
    }
  }, [isAuthenticated, authLoading]);

  // Reactivate on visibility
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const onVis = () => {
      if (document.visibilityState === "visible") {
        startLocationTracking();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    const consent = localStorage.getItem("instant-tours-cookie-consent");
    if (!consent) setShowCookieBanner(true);
  }, []);

  if (authLoading) {
    return (
      <DesktopGate allowDesktop={allowDesktop}>
        <FullPageLoader label={t("Starting Instant Tours…")} />
      </DesktopGate>
    );
  }

  return (
    <DesktopGate allowDesktop={allowDesktop}>
    <LanguageGate>
      {/* Guide background refresher — mounted here (inside the auth
          guard's render tree) so it's always running for logged-in
          users regardless of which tab they're viewing. It does NOT
          depend on the Guide page being mounted. Fire-and-forget
          requests to refreshGuide; content arrives via Firestore
          listener when ready. Chat path is completely independent. */}
      <GuideBackgroundMount isAuthenticated={isAuthenticated} />

        {/* Visit-to-feed bridge — observes the user's visits collection
            and creates a feed_item for each new verified visit (kind=
            'postcard'). Idempotent across sessions via a feed_item_id
            stamp on the visit doc. See feed/useVisitToFeedBridge.ts. */}
        <VisitToFeedMount isAuthenticated={isAuthenticated} />

        {/* Username claim modal — shown to newly-registered users
            who haven't yet picked a username. Self-gates internally
            so it's safe to mount unconditionally inside the auth
            tree. */}
        <UsernameClaimModal />

        {/* Onboarding dialog — shown to newly-registered users (and
            any registered user who hasn't yet completed their travel
            profile). Hides automatically once they finish or skip. */}
        <OnboardingDialog
          open={showOnboarding}
          onClose={() => setShowOnboarding(false)}
        />

        {/* Global auth modal host. Mounted exactly once at App level so
            it survives the unmount of any Menu/Popover/Drawer that may
            have triggered it. Components throughout the app open it
            via useAuthStore().openAuthModal(). Critical for the Google
            sign-in flow because the OAuth popup steals focus, which
            closes the avatar dropdown that hosts the Login entry. */}
        <AuthModalHost />
      <Routes>
        <Route path="/" element={<RootLandingRoute />} />
        <Route path="/new" element={<Navigate to="/chat" replace />} />

        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:conversationId"
          element={
            <ProtectedRoute>
              <ChatRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide"
          element={
            <ProtectedRoute>
              <GuideRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discover"
          element={
            <ProtectedRoute>
              <DiscoverRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <FeedRoute />
            </ProtectedRoute>
          }
        />
        {/* Legacy alias — old /postcards bookmarks land on Feed.
            Postcards are now an item type inside the feed. */}
        <Route
          path="/postcards"
          element={
            <ProtectedRoute>
              <FeedRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/map"
          element={
            <ProtectedRoute>
              <MapRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collections"
          element={
            <ProtectedRoute>
              <CollectionsRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/curator-log"
          element={
            <ProtectedRoute>
              <FeatureRoute flag="memoryLog">
                <CuratorLogRoute />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        {/* Profile routes — accept either a uid (default) or a
            @username form. ProfilePage resolves @username via the
            /usernames lookup; both forms render the same page. */}
        <Route
          path="/u/:handle"
          element={
            <ProtectedRoute>
              <ProfileRoute />
            </ProtectedRoute>
          }
        />

        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/guide/:shareId" element={<SharedCardPage />} />
        <Route path="/settings" element={<Settings />} />

        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Routes>

      {showCookieBanner && (
        <CookieBanner
          onAccept={() => setShowCookieBanner(false)}
          onDecline={() => setShowCookieBanner(false)}
        />
      )}
    </LanguageGate>
    </DesktopGate>
  );
}

/**
 * Guide refresher — lives outside the route tree so it runs regardless
 * of which page is mounted. Only runs when user is authenticated.
 */
function GuideBackgroundMount({ isAuthenticated }: { isAuthenticated: boolean }) {
  if (!isAuthenticated) return null;
  return <GuideBackgroundRunner />;
}

function GuideBackgroundRunner() {
  useGuideBackground();
  useGuideFeedBackground();
  return null;
}

function App() {
  // Google Maps browser key. Public-by-design; restrict by HTTP
  // referrer in the GCP console rather than keeping it secret.
  // When missing, MiniMap silently falls back to a gradient
  // placeholder — the rest of the app still works.
  const mapsKey =
    (import.meta.env as any).VITE_GOOGLE_MAPS_BROWSER_KEY || "";

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <APIProvider apiKey={mapsKey} libraries={["routes", "marker"]}>
          <UIProvider>
            {/*
              BrowserRouter lives at the top of the tree (was
              previously inside AppContent) so the public-path
              branch in AppContent can use useLocation() and
              react to client-side navigation. Previously it
              read window.location.pathname at render time,
              which only updated on hard reload — clicking a
              link to /privacy from inside the app didn't
              re-evaluate the gate decision.
            */}
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </UIProvider>
        </APIProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
